import { Avatar, Box, Button, ColumnDatas, Tooltip, Typography, getColor } from "fds";
import utilsCommon from "../../shared/utils/common";
import { ActiveUser, DeptInfo, User } from "../../types/adminDept";
import { Member } from "../../types/workgroup";
import WorkgroupManagerIcon from "../../components/WorkgroupManagerIcon";

type TFunc = (key: string, options?: any) => string;

// ────────────────────────────────────────────────────────────────────────────────
// 내부 사용자 테이블 변환
// ────────────────────────────────────────────────────────────────────────────────
interface InternalUserTransformOptions {
  targetData: { state: string };
  setChangeManager: (user: User) => void;
  setEditUserInfo: (user: any) => void;
  setTargetData: (data: any) => void;
  t: TFunc;
}

export function transformInternalUserList(
  data: any[] | undefined,
  check: { handleClick: (id: string) => void },
  options: InternalUserTransformOptions
): ColumnDatas[] {
  const { targetData, setChangeManager, setEditUserInfo, setTargetData, t } = options;
  if (!data || data.length === 0) return [];

  return data.map((user: any) => {
    const cols: any[] = [];

    cols.push({
      columnText: (
        <Box alignItems="center">
          <Avatar
            uniqueCode={utilsCommon.getUserColor(1)}
            name={user?.name ?? user?.userName}
            classes="avatarManager mg mr-8"
            subLeftIcon={
              (user?.id ?? user?.userId) === user?.dept?.managerId ? (
                <WorkgroupManagerIcon
                  style={{ position: "absolute", bottom: "-5px", insetInlineEnd: "-12px" }}
                />
              ) : undefined
            }
          />
          {utilsCommon.getNameWithId(user?.name ?? user?.userName, user?.id ?? user?.userId)}
        </Box>
      ),
    });

    cols.push({
      columnText: user?.dept?.name ?? user?.depts?.primary?.name ?? user?.deptName ?? "",
    });

    if (window.eco.serviceType !== "OCI") {
      cols.push({ columnText: user?.role?.name });
      cols.push({ columnText: user?.position?.name });
      cols.push({ columnText: user?.email });
      cols.push({ columnText: user?.phoneNumber });
      cols.push({
        columnText:
          targetData.state === "deptManagerUpdate" ? (
            user.id !== user.dept.managerId ? (
              <Button
                label={t("admin.dept.select")}
                btnStyle="line"
                size="sm"
                onClick={() => setChangeManager(user)}
              />
            ) : (
              ""
            )
          ) : (
            <Button
              label={t("admin.dept.modify")}
              btnStyle="line"
              size="sm"
              onClick={() => {
                setEditUserInfo(user);
                setTargetData({ id: user.id, name: user.name, type: "user", state: "deptUserUpdate" });
              }}
            />
          ),
      });
    } else {
      cols.push({
        columnText: user.regDate ? utilsCommon.renderDateFormat(user.regDate) : "-",
      });
      cols.push({
        columnText:
          user.status === 1
            ? t("admin.manage.inUse")
            : user.status === 2
            ? t("admin.manage.wait")
            : t("admin.manage.completedMember"),
      });
    }

    return {
      data: cols,
      id: user?.id ?? user?.userId,
      checkBoxClick:
        targetData.state === "deptManagerUpdate"
          ? undefined
          : () => check.handleClick(user.id ?? user.userId),
    };
  });
}

// ────────────────────────────────────────────────────────────────────────────────
// 외부 사용자 테이블 변환
// ────────────────────────────────────────────────────────────────────────────────
interface ExternalUserTransformOptions {
  setEditExternalUserInfo: (user: any) => void;
  setTargetData: (data: any) => void;
  reInviteMember: (userId: string) => void;
  t: TFunc;
}

export function transformExternalUserList(
  data: Member[] | undefined,
  check: { handleClick: (id: string) => void },
  options: ExternalUserTransformOptions
): ColumnDatas[] {
  const { setEditExternalUserInfo, setTargetData, reInviteMember, t } = options;
  if (!data || data.length === 0) return [];

  return data.map((user: Member) => {
    const cols: any[] = [];

    cols.push({
      columnText: (
        <Box alignItems="center">
          <Avatar
            uniqueCode={utilsCommon.getUserColor(user.userType, user.status, user.userName)}
            name={user.userName ? user.userName : user.userId}
            icon={user?.userName ? undefined : "user"}
            classes="mg mr-8"
          />
          <Tooltip message={user.userId}>
            <Typography ellipsis>{user.userId}</Typography>
          </Tooltip>
        </Box>
      ),
    });

    cols.push({ columnText: user.userName && user.userType !== 3 ? user.userName : "-" });
    cols.push({ columnText: user.deptName ?? user.companyName ?? "-" });

    if (window.eco.serviceType !== "OCI") {
      cols.push({ columnText: user.phoneNumber });
    }

    cols.push({
      columnText: utilsCommon.changeStatusCodeToStringForInfo(user.userType, user.status),
    });

    cols.push({
      columnText:
        window.eco.serviceType === "OCI" ? (
          user.status === 2 ? (
            <Typography
              exactColor={getColor("COLOR_TEXT_BRAND")}
              classes="cursor pointer"
              type="caption_rg"
              onClick={() => reInviteMember(user.userId)}
            >
              {t("admin.manage.inviteAgain")}
            </Typography>
          ) : (
            ""
          )
        ) : (user.status === 1 || user.status === 3) && user.userType !== 3 ? (
          <Button
            label={t("admin.dept.modify")}
            btnStyle="line"
            size="sm"
            onClick={() => {
              setEditExternalUserInfo({
                deptName: user.deptName,
                etc: user.etc,
                phoneNumber: user.phoneNumber,
                status: user.status,
                userId: user.userId,
                userName: user.userName,
                userType: user.userType,
              });
              setTargetData({
                id: user.userId,
                name: user.userName,
                type: "externalUser",
                state: "deptUserUpdate",
              });
            }}
          />
        ) : (
          ""
        ),
    });

    return {
      data: cols,
      id: user.userId,
      checkBoxClick: () => check.handleClick(user.userId),
    };
  });
}

// ────────────────────────────────────────────────────────────────────────────────
// 활성/비활성/Lite 사용자 테이블 변환
// ────────────────────────────────────────────────────────────────────────────────
export function transformActiveUserList(
  data: ActiveUser[],
  check: { handleClick: (id: string) => void },
  userStatus: string,
  leftTab: number,
  t: TFunc
): ColumnDatas[] {
  if (!data || data.length === 0) return [];

  return data.map((user: any) => {
    let cols: any[];

    if (userStatus.includes("INACTIVE")) {
      cols = [
        {
          columnText: (
            <Box alignItems="center">
              <Avatar
                uniqueCode={utilsCommon.getUserColor(user.userType, 2, user.userName)}
                name={user.userName ? user.userName : user.userId}
                icon={user?.userName ? undefined : "user"}
                classes="mg mr-8"
              />
              <Tooltip message={user.userId}>
                <Typography ellipsis>{user.userId}</Typography>
              </Tooltip>
            </Box>
          ),
        },
      ];
    } else if (userStatus.includes("LITE")) {
      cols = [
        {
          columnText: (
            <Box alignItems="center">
              <Avatar
                uniqueCode={utilsCommon.getUserColor(
                  leftTab === 0 ? 1 : user.userType,
                  user.userStatus,
                  user.userName
                )}
                name={user.userName ? user.userName : user.userId}
                icon={user?.userName ? undefined : "user"}
                classes="mg mr-8"
              />
              <Tooltip message={user.userId}>
                <Typography ellipsis>{user.userId}</Typography>
              </Tooltip>
            </Box>
          ),
        },
        {
          columnText: utilsCommon.renderDateFormat(
            window.eco.serviceType === "OCI"
              ? user.regDate
              : user.lastLoginTime ?? (user.updatedTime === 0 ? user.registedTime : user.updatedTime)
          ),
        },
      ];
    } else {
      cols = [
        {
          columnText: (
            <Box alignItems="center">
              <Avatar
                uniqueCode={utilsCommon.getUserColor(
                  leftTab === 0 ? 1 : user.userType,
                  user.userStatus,
                  user.userName
                )}
                name={user.userName ? user.userName : user.userId}
                icon={user?.userName ? undefined : "user"}
                classes="mg mr-8"
              />
              <Tooltip message={user.userId}>
                <Typography ellipsis>{user.userId}</Typography>
              </Tooltip>
            </Box>
          ),
        },
        { columnText: user.userType === 3 ? "-" : user.userName },
        {
          columnText: utilsCommon.renderDateFormat(
            window.eco.serviceType === "OCI"
              ? user.regDate
              : user.updatedTime === 0
              ? user.registedTime
              : user.updatedTime
          ),
        },
      ];
    }

    return {
      data: cols,
      id: user.userId,
      checkBoxClick: () => check.handleClick(user.userId),
    };
  });
}

// ────────────────────────────────────────────────────────────────────────────────
// 부서 목록 테이블 변환
// ────────────────────────────────────────────────────────────────────────────────
export function transformDeptList(data: DeptInfo[] | undefined, t: TFunc): ColumnDatas[] {
  if (!data || data.length === 0) return [];

  return data.map((dept: DeptInfo) => ({
    data: [
      { columnText: "" },
      {
        columnText: (
          <Box alignItems="center">
            <Avatar uniqueCode={dept.code} name={dept.name} classes="mg mr-8" shape="workgroup" />
            {dept.name}
          </Box>
        ),
      },
      {
        columnText: dept.managerId
          ? utilsCommon.getNameWithId(dept.managerName, dept.managerId)
          : t("admin.dept.unspecified"),
      },
      { columnText: dept.deptCount },
      { columnText: dept.userCount },
    ],
    id: dept.code,
  }));
}
